import {Injectable} from '@angular/core';
import {environment} from '@Env/environment';

import {HttpClient, HttpParams} from '@angular/common/http';
import {DaoGeneric} from '@Services/base/daoService';

import {Observable} from 'rxjs';
import {ListReference, PopulationEnum, StateEnum, TransitionEnum, Transitions} from '../index';
import {Dossiers} from './dossiers.model';
import {DecisionEnum} from '../workflow/decision-commission.enum';
import {catchError, map} from 'rxjs/operators';
import {rolesDar} from '../roles/roles-dar';

import * as moment from 'moment';
import {StateLabel} from '../workflow/states-labels.interface';
import {Entite} from '@Services/gestion-universite/entite/entite.model';

/**
 * Dossiers services pour demandeurs / rôles et changement d'état
 */
@Injectable({ providedIn: 'root' })
export class DarDossiersService extends DaoGeneric<Dossiers> {
  private url = `${environment.baseUrl}/dar/dossiers/`;

  typesDemandes: ListReference[] = [
    {id: 2, label: 'BIATOSS'},
    {id: 4, label: 'Enseignant'}
  ];

  remunerations: ListReference[] = [
    {id: 1, label: 'Indicaire'},
    {id: 2, label: 'Horaire'}
  ];

  plafondsEmploi: ListReference[] = [
    {id: 1, label: 'Plafond 1 (Etat)'},
    {id: 2, label: 'Plafond 2 (Propre)'}];
  /**
   * State ==> Roles : pour vérification des boutons transitions activés ou non selon rôles
   *
   * ie : les rôles concernés par les boutons de passage d'état, si cela match alors affichage bouton activé
   */
  buttonVerifsStateRoles: Map<StateEnum, rolesDar[]> = new Map<StateEnum, rolesDar[]>([
    [StateEnum.masse_salariale, [rolesDar.masse_salariale]],
    [StateEnum.sbtcp, [rolesDar.sbtcp]],
    [StateEnum.drh, [rolesDar.drh]],
    [StateEnum.commission, [rolesDar.commission]],
    [StateEnum.refus, [rolesDar.commission]],
    [StateEnum.commission_decision, [rolesDar.commission]],
    [StateEnum.spta, [rolesDar.spta]],
    [StateEnum.recrutement, [rolesDar.recrutement]],
    [StateEnum.publication, [rolesDar.recrutement]],
    [StateEnum.spta_traitement, [rolesDar.spta_traitement]],
    [StateEnum.spte, [rolesDar.spte]],
    [StateEnum.recrutement_spte, [rolesDar.recrutement_spte]]
    ]);

  /**
   * A partir de ces états, la période ou l'emploi n'est plus modifiable
   * croisés avec la periodeRoles
   */
  periodeOrEmploiStates: StateEnum[] = [
    StateEnum.commission, StateEnum.commission_decision,
    StateEnum.spta, StateEnum.spta_traitement,
    StateEnum.spte,
    StateEnum.publication,
    StateEnum.refus, StateEnum.clos];

  /**
   * la partie periode n'est modifiable que pour les roles suivants
   * croisés avec l'état du dossier (cf. edit-stepper.component IsInStates)
   */
  periodeRoles: rolesDar[] = [rolesDar.masse_salariale, rolesDar.sbtcp, rolesDar.drh, rolesDar.commission];

  constructor(private httpClient: HttpClient) {
    super(httpClient);
  }

  getRootUrl(): string {
    return this.url;
  }

  /**
   * Les états après DRH
   */
  get statesGtDrh() {
    return this.periodeOrEmploiStates;
  }

  /**
   * La période est-elle à désactiver ?
   * selon l'état du dossier et du rôle du connecté
   * => à partir de la commission, la période n'est plus modifiable
   * => avant commission, uniquement pour les rôles ms, sbtcp, drh, commission
   *
   * @param state
   * @param roles
   */
  isDisablePeriode(state: StateEnum, roles: rolesDar[] = []): boolean {
    let canRole = false;
    let can = !this.isInStates(state, this.periodeOrEmploiStates);

    roles.forEach((_role) => {
      if (this.periodeRoles.some((_prole) => _prole === _role)) {
        canRole = true;
      }
    });

    can = can && canRole;

    return ! can;
  }

  /**
   * A partir de commission, l'emploi n'est plus modifiable
   * TODO : avant à partir de SPTA, voir avecc Eric
   *
   * @param state
   */
  canEditEmploi(state: StateEnum) {
    return ! this.periodeOrEmploiStates.some((_state) => _state === state);
  }

  private _changeState(uuid: string, extraQS: Map<string, string>): Observable<Dossiers> {
    const params =  this._getUrlHttpParams(extraQS);
    return this.httpClient
      .get<Dossiers>(`${this.url}${uuid}/transitionGo/`, { params: params } )
      .pipe(catchError((error: any) => this._throwObservable(error)));
  }

  /**
   * Changement d'état d'un dossier
   * @param uuid uuid/id du dossier
   * @param transition la transition à exécuter
   */
  changeState(uuid: string, transition: TransitionEnum): Observable<Dossiers> {
    if (transition !== TransitionEnum.none) {
      const extraQS: Map<string, string> = new Map<string, string>();
      extraQS.set('transition', transition);
      return this._changeState(uuid, extraQS);
    }
  }

  /**
   * Archivage d'un dossier
   * @param uuid uuid/id du dossier
   * @param comment raison de l'archivage
   */
  changeStateToArchive(uuid: string, comment: string): Observable<Dossiers> {
    const extraQS: Map<string, string> = new Map<string, string>();
    extraQS.set('transition', TransitionEnum.archived);
    extraQS.set('comment', comment);
    return this._changeState(uuid, extraQS);
  }

  /**
   * Liste des transitions de l'état courant d'un dossier
   * @param uuid uuid/id du dossier
   */
  listTransitions(uuid: string): Observable<Transitions[]> {
    return this.httpClient.get<Transitions[]>(`${this.url}${uuid}/transitions/` );
  }

  contacts(action: 'add' | 'delete', dossier: string, contact: string) {
    const params = this._getUrlHttpParams(new Map([['contact', contact],]));
    const action_2_http = {
      add: this.httpClient.patch<Dossiers>(`${this.url}${dossier}/contacts/`, null, {params: params}),
      delete: this.httpClient.delete<Dossiers>(`${this.url}${dossier}/contacts/`, {params: params}),
    };
    return action_2_http[action];
  }

  /**
   * Obtient les types de demandes (BIATOSS ou Enseignant)
   */
  getTypesDemandes()  {
    return this.typesDemandes;
  }

  getLabelTypeDemande(type_demande: PopulationEnum) {
    const find = this.typesDemandes.find((_type) => _type.id === type_demande);

    if (find) {
      return find.label;
    }

    return '';
  }

  /**
   * Obtient les types de rémunérations
   */
  getRemunerations()  {
    return this.remunerations;
  }

  /**
   * Obtient les plafonds d'emploi : "id" et libellés
   */
  getPlafondsEmploi(): ListReference[] {
    return this.plafondsEmploi;
  }
  /**
   * Passage de tous les dossiers en "commission_decision" en SPTA ou SPTE
   */
  batchDossiersCommissionDecision() {
    return this.httpClient.get(`${this.url}batch_commission_decision/`);
  }

  /**
   * Libellé de la décision commission (DecisionEnum => libellé)
   * @param dossier
   */
  public getDecision(dossier: Dossiers) {
    if (! dossier.decision_commission) {
      return '';
    }

    switch (dossier.decision_commission) {
      case DecisionEnum.validee:
        return 'Validé';
      case DecisionEnum.validee_partiellement:
        return 'Validé partiellement';
      case DecisionEnum.refus:
        return 'Refusé';
    }
  }

  /**
   * Obtient le mapping state / état => rôles qui peuvent agit dessus
   */
  public getStateForRoles() {
    return this.buttonVerifsStateRoles;
  }

  /**
   * Export PDF de la demande pour le demandeur
   * @param {Dossiers} dossier
   * @param fileName
   * @param templateName
   * @param typeDownload
   */
  public exportPdfDemandeur(dossier: Dossiers, typeDownload: string = 'pdf',
                            fileName: string = 'export_dar_demande',
                            templateName: string = 'dar_export_pdf_demandeur'
                            ): Observable<Blob> {
    const url = `${this.url}${dossier.uuid}/export_pdf/`;
    let params = new HttpParams();
    params = params.set('template_name', templateName)
      .set('file_name', fileName)
      .set('type_download', typeDownload);

    return this.httpClient.get(url, {
      params,
      responseType: 'blob'
    }).pipe(map((data: any) => new Blob([data])));
  }

  /**
   * Transformation Date string (YYYY-MM-DD => Date())
   * TODO : peut-on ne pas utiliser moment ?
   * @param dt
   */
  getDateFromString(dt: string) {
    let dtReturn = null;
    if (dt) {
      dtReturn = moment(dt).toDate();
    }
    return dtReturn;
  }

  /**
   * Nb jours entre 2 dates
   * @param {Date} dateDebut
   * @param {Date} dateFin
   * @param {boolean} included : date début incluse, par défaut oui
   */
  getDaysBetweenDates(dateDebut: Date, dateFin: Date, included = true) {
    if (dateDebut && dateFin) {
      const oneDay = 1000 * 60 * 60 * 24;
      const diffDates = dateFin.getTime() - dateDebut.getTime();

      let nbDays = Math.round(diffDates / oneDay);

      if (included) {
        nbDays += 1;
      }

      return nbDays;
    }

    return 0;
  }

  /**
   * L'état appartient-il à un des états de la liste ?
   * @param state
   * @param states
   * @private
   */
  isInStates(state: StateEnum, states: StateEnum[]) {
    return states.some((_state) => _state === state);
  }

  /**
   * Libellé court de l'état d'un dossier
   * @param {StateEnum} state
   * @param {string} defaultTitle : état par défaut si non trouvé
   * @private
   */
  private _stateShortTitle(state: StateEnum, defaultTitle = '') {
    switch (state) {
      case StateEnum.sbtcp:
        return 'SBTCP';
      case StateEnum.masse_salariale:
        return 'Masse salariale';
      case StateEnum.drh:
        return 'DRH';
      case StateEnum.clos:
        return 'Clos';
      case StateEnum.refus:
        return 'Refus';
      case StateEnum.demande:
        return 'Création';
      case StateEnum.commission:
        return 'Commission';
      case StateEnum.commission_decision:
        return 'Décision commission';
      case StateEnum.spta:
        return 'SPTA';
      case StateEnum.spte:
        return 'SPTE';
      case StateEnum.spta_traitement:
        return 'SPTA gestionnaire';
      case StateEnum.publication:
        return 'Publication';
      case StateEnum.recrutement:
        return 'Recrutement SPTA';
      case StateEnum.recrutement_spte:
        return 'Recrutement SPTE';
      case StateEnum.archived:
        return 'Sans suite';
      default:
        return defaultTitle;
    }
  }
  /**
   * Libellé long de l'état d'un dossier
   * @param {StateEnum} state
   * @param {string} defaultTitle : libellé par défaut si non trouvé
   * @private
   */
  private _stateLongTitle(state: StateEnum, defaultTitle = '') {
    switch (state) {
      case StateEnum.sbtcp:
        return 'En SBTCP';
      case StateEnum.masse_salariale:
        return 'En masse salariale';
      case StateEnum.drh:
        return 'En validation DRH';
      case StateEnum.clos:
        return 'Clos';
      case StateEnum.refus:
        return 'Refus';
      case StateEnum.demande:
        return 'En création';
      case StateEnum.commission:
        return 'En commission';
      case StateEnum.spta:
        return 'Validation SPTA';
      case StateEnum.spte:
        return 'En SPTE';
      case StateEnum.spta_traitement:
        return 'Gestionnaire SPTA';
      case StateEnum.publication:
        return 'En publication';
      case StateEnum.recrutement:
        return 'En recrutement SPTA';
      case StateEnum.recrutement_spte:
        return 'En recrutement SPTE';
      case StateEnum.archived:
        return 'Sans suite';
      default:
        return defaultTitle;
    }
  }
  /**
   * Obtient le libellé de l'état pour un dossier
   * @param dossier
   */
  getStateTitle(dossier: Dossiers) {
    return this._stateLongTitle(dossier.state, dossier.state_title);
  }

  /**
   * Libellé vers l'étape cible
   * @param {TransitionEnum} transitionState
   */
  toStateLabel(transitionState: TransitionEnum) {
    const _toStateLabel: Map<TransitionEnum, string> = new Map<TransitionEnum, string>([
      [TransitionEnum.validation_masse_salariale, 'de votre demande'],
      [TransitionEnum.validation_sbtcp, 'SBTCP'],
      [TransitionEnum.validation_drh, 'DRH'],
      [TransitionEnum.validation_spta, 'SPTA'],
      [TransitionEnum.validation_commission, 'COMMISSION'],
      [TransitionEnum.validation_acceptee_commission, 'ACCEPTATION'],
      [TransitionEnum.validation_partielle_commission, 'ACCEPTATION PARTIELLE'],
      [TransitionEnum.demande_refusee, 'REFUS'],
      [TransitionEnum.annulation_decision_commission, 'RETOUR en commission'],
      [TransitionEnum.recrutement_interne_sans_publication, 'RECRUTEMENT sans publication'],
      [TransitionEnum.recrutement_externe, 'RECRUTEMENT externe'],
      [TransitionEnum.recrutement_interne, 'RECRUTEMENT interne'],
      [TransitionEnum.recrutement_interne_externe, 'RECRUTEMENT interne externe'],
      [TransitionEnum.recrutement_publication, 'PUBLICATION'],
      [TransitionEnum.publication_spta_traitement, 'TRAITEMENT'],
      [TransitionEnum.spta_traitement_clos, 'FINALISATION'],
      [TransitionEnum.recrutement_spte, 'RECRUTEMENT SPTE'],
      [TransitionEnum.spte_traitement_clos, 'FINALISATION SPTE']
    ]);
    return _toStateLabel.get(transitionState) || '';
  }
  /**
   * Liste des états possibles au format "humain"
   * @param {boolean} short : par défaut le libellé court, long sinon
   * @param {StateEnum[]} forOnlyStates : filtre uniquement sur ces statuts
   * @return state / nom court par défaut
   */
  getStateTitles(short: boolean = true, forOnlyStates: StateEnum[] = null): StateLabel[] {
    return Object
      .values(StateEnum)
      .filter((value) =>
        value !== StateEnum.none &&
        (!forOnlyStates || forOnlyStates.includes(value))
      )
      .map((value) => ({
        id: value,
        label: short
          ? this._stateShortTitle(value)
          : this._stateLongTitle(value),
      }));
  }

  /**
   * Liste des directions / composantes rattacheés à des dossiers
   * @constructor
   */
  DirCmpDossiers(): Observable<Entite[]> {
    const url = `${this.url}dir_cmp_dossiers/`;
    return this.httpClient.get<Entite[]>(url);
  }

  getShortLabel(state: StateEnum): string {
    return this._stateShortTitle(state);
  }
  public changeDemandeur(dossier, demandeur) {
    const url = `${this.url}${dossier}/new_demandeur/`;
    const params = this._getUrlHttpParams(new Map([['demandeur', demandeur], ]));
    return this.httpClient.patch<Dossiers>(url, null, {params: params});
  }
  public getStatesTransitionLogs(dossier: Dossiers) {
    const url = `${this.url}${dossier.uuid}/transitions_logs/`;
    return this.httpClient.get<Dossiers>(url);
  }

  /**
   * Liste des states utilisés dans les dossiers d'un demandeur
   */
  public statesMesDemandes() {
    const _url = `${this.url}states_mes_demandes/`;
    return this.httpClient.get<StateEnum[]>(_url);
  }
}
